import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Keeps a component mounted during an exit transition after `visible` becomes false.
 * Returns `shouldRender` (whether to render at all) and `exiting` (whether the exit
 * transition is in progress). Call `onTransitionEnd` when the CSS transition completes
 * to unmount.
 *
 * @example
 * ```tsx
 * const { shouldRender, exiting, onTransitionEnd } = useExitTransition(visible);
 * if (!shouldRender) return null;
 * return (
 *     <div
 *         className={exiting ? "scale-0" : "scale-100"}
 *         onTransitionEnd={onTransitionEnd}
 *     >
 *         Content
 *     </div>
 * );
 * ```
 */
export function useExitTransition(visible: boolean) {
    const [rendered, setRendered] = useState(visible);
    const [exiting, setExiting] = useState(false);
    const prevVisible = useRef(false);

    useEffect(() => {
        if (visible) {
            setRendered(true);
        } else if (prevVisible.current) {
            setExiting(true);
        }
        prevVisible.current = visible;
        // rendered must be in the dep array: the extra effect run after
        // setRendered(true) ensures prevVisible.current is set to true before
        // a dismiss can flip visible to false, so the true→false transition
        // is always detected.
    }, [visible, rendered]);

    const onTransitionEnd = useCallback((_e?: unknown) => {
        setRendered(false);
        setExiting(false);
    }, []);

    return { shouldRender: visible || rendered, exiting, onTransitionEnd };
}
